import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, CircleNotch, Lightning, Target } from "@phosphor-icons/react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import ClassMixLast200 from "./ClassMixLast200";
import WindowNarrative from '../components/WindowNarrative';
import { NARRATIVES } from '../components/windowNarratives';

const API = process.env.REACT_APP_BACKEND_URL;

const classColors = {
  High:   { bg: "bg-red-500", text: "text-white", light: "bg-red-50 text-red-700 border-red-200", tag: "bg-red-100 text-red-700", letter: "A", display: "Above" },
  Medium: { bg: "bg-amber-500", text: "text-white", light: "bg-amber-50 text-amber-700 border-amber-200", tag: "bg-amber-100 text-amber-700", letter: "B", display: "Balanced" },
  Low:    { bg: "bg-blue-500", text: "text-white", light: "bg-blue-50 text-blue-700 border-blue-200", tag: "bg-blue-100 text-blue-700", letter: "L", display: "Low" },
};

export default function WindowClassMix() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/analysis/class-mix`).then(res => { setData(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-gray-500">Failed to load</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="back-btn"><ArrowLeft size={20} /></button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">W55: Class Mix Projections</h1>
            <p className="text-xs text-gray-500">{data.year} draw class analysis — {data.cy_draws} draws | Based on appearance frequency classes</p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">


        <WindowNarrative windowId="W55" {...NARRATIVES.W55} className="mb-5" />
        <Tabs defaultValue="cy-class" className="w-full" data-testid="class-mix-tabs">
          <TabsList className="bg-white border border-gray-200 p-1 mb-4" data-testid="class-mix-tablist">
            <TabsTrigger value="cy-class" className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid="tab-cy-class">
              {data.year} Class (CY)
            </TabsTrigger>
            <TabsTrigger value="last-200" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white font-bold" data-testid="tab-last-200">
              Last 200
            </TabsTrigger>
          </TabsList>

          <TabsContent value="cy-class" className="space-y-6 mt-0">
        {/* Class Legend */}
        <div className="flex flex-wrap gap-3 items-center" data-testid="class-legend">
          <span className="text-xs font-bold text-gray-500 uppercase">ABL Classes:</span>
          {[["High", "5+ times"], ["Medium", "3-4 times"], ["Low", "0-2 times"]].map(([cls, desc]) => (
            <div key={cls} className="flex items-center gap-1.5">
              <span className={`inline-flex w-6 h-6 rounded-full ${classColors[cls].bg} ${classColors[cls].text} items-center justify-center font-bold text-[10px]`}>{classColors[cls].letter}</span>
              <span className="text-xs text-gray-600"><strong>{classColors[cls].display}</strong> ({desc})</span>
              <span className="text-[10px] text-gray-400">— {data.class_summary[cls]?.count || 0} numbers</span>
            </div>
          ))}
        </div>

        {/* Number Grid by Class */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="class-grid">
          {["High", "Medium", "Low"].map(cls => (
            <div key={cls} className={`border rounded-lg p-4 ${classColors[cls].light}`}>
              <div className="flex items-center gap-2 mb-3">
                <span className={`inline-flex w-7 h-7 rounded-full ${classColors[cls].bg} ${classColors[cls].text} items-center justify-center font-bold text-xs`}>{classColors[cls].letter}</span>
                <span className="font-bold text-sm">{classColors[cls].display}</span>
                <span className="text-[10px] text-gray-400 ml-auto">{data.class_summary[cls]?.count} numbers</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {data.number_data.filter(n => n.class === cls).map(n => (
                  <div key={n.number} className="flex flex-col items-center">
                    <span className={`inline-flex w-8 h-8 rounded-full ${classColors[cls].bg} ${classColors[cls].text} items-center justify-center font-mono font-bold text-xs`}>{n.number}</span>
                    <span className="text-[9px] text-gray-500 mt-0.5">{n.times}x</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Top Class Mix Patterns */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="patterns">
          <div className="bg-[#002FA7] px-5 py-3 flex items-center gap-3">
            <Lightning size={18} weight="fill" className="text-white" />
            <h2 className="text-white font-bold text-lg">Most Common Class Mix Patterns in {data.year}</h2>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {data.top_patterns.map((tp, i) => (
                <div key={`tp-${tp.pattern}-${i}`} className={`border rounded-lg p-3 text-center ${i === 0 ? 'ring-2 ring-[#002FA7] bg-blue-50' : 'bg-gray-50'}`} data-testid={`pattern-${i}`}>
                  <div className="flex justify-center gap-1 mb-2">
                    {tp.pattern.split("-").map((cls, ci) => (
                      <span key={`${tp.pattern}-${ci}-${cls}`} className={`inline-flex w-6 h-6 rounded-full ${classColors[cls]?.bg || 'bg-gray-300'} text-white items-center justify-center font-bold text-[9px]`}>{classColors[cls]?.letter || cls[0]}</span>
                    ))}
                  </div>
                  <div className="font-bold text-lg text-gray-800">{tp.count}x</div>
                  <div className="text-[10px] text-gray-500">{tp.pct}% of draws</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Last CY Draws Class Analysis */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="draw-analysis">
          <div className="bg-gray-800 px-5 py-3">
            <h2 className="text-white font-bold text-lg">Recent {data.year} Draws — Class Breakdown</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-2 text-left text-[10px] uppercase text-gray-600 font-bold">Draw</th>
                  <th className="px-3 py-2 text-center text-[10px] uppercase text-gray-600 font-bold">Ball 1</th>
                  <th className="px-3 py-2 text-center text-[10px] uppercase text-gray-600 font-bold">Ball 2</th>
                  <th className="px-3 py-2 text-center text-[10px] uppercase text-gray-600 font-bold">Ball 3</th>
                  <th className="px-3 py-2 text-center text-[10px] uppercase text-gray-600 font-bold">Ball 4</th>
                  <th className="px-3 py-2 text-center text-[10px] uppercase text-gray-600 font-bold">Ball 5</th>
                  <th className="px-3 py-2 text-center text-[10px] uppercase text-amber-600 font-bold">LS 1</th>
                  <th className="px-3 py-2 text-center text-[10px] uppercase text-amber-600 font-bold">LS 2</th>
                  <th className="px-3 py-2 text-center text-[10px] uppercase text-gray-600 font-bold">Class Mix</th>
                  <th className="px-3 py-2 text-center text-[10px] uppercase text-gray-600 font-bold">Score</th>
                </tr>
              </thead>
              <tbody>
                {[...data.draw_class_patterns].reverse().map((dcp, idx) => {
                  return (
                  <tr key={dcp.draw_number} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-2 border-b">
                      <div className="font-bold text-gray-800">{dcp.draw_number}</div>
                      <div className="text-[10px] text-gray-400">{dcp.draw_date}</div>
                    </td>
                    {dcp.ball_details.map((bd, bi) => (
                      <td key={bi} className="px-3 py-2 border-b text-center">
                        <div className="flex flex-col items-center gap-0.5">
                          <span className={`inline-flex w-9 h-9 rounded-full ${classColors[bd.class]?.bg} ${classColors[bd.class]?.text} items-center justify-center font-mono font-bold text-sm`}>{bd.number}</span>
                          <span className={`text-[9px] font-bold px-1.5 rounded ${classColors[bd.class]?.tag}`}>{classColors[bd.class]?.letter || bd.class[0]} {bd.times_before}x</span>
                        </div>
                      </td>
                    ))}
                    {(dcp.ls_details || []).map((ls, li) => (
                      <td key={`ls-${li}`} className="px-3 py-2 border-b text-center">
                        <div className="flex flex-col items-center gap-0.5">
                          <span className="inline-flex w-9 h-9 rounded-full bg-amber-500 text-white items-center justify-center font-mono font-bold text-sm">{ls.number}</span>
                          <span className="text-[9px] font-bold px-1.5 rounded bg-amber-100 text-amber-700">{ls.times_before}x</span>
                        </div>
                      </td>
                    ))}
                    <td className="px-3 py-2 border-b text-center">
                      <div className="flex justify-center gap-1">
                        {Object.entries(dcp.class_counts).map(([cls, cnt]) => (
                          <span key={cls} className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${classColors[cls]?.tag}`}>{classColors[cls]?.letter || cls[0]}:{cnt}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2 border-b text-center">
                      <div className="flex flex-col items-center gap-0.5">
                        <span className={`font-bold text-sm font-mono ${dcp.verdict === 'EXCELLENT' ? 'text-green-600' : dcp.verdict === 'GOOD' ? 'text-blue-600' : dcp.verdict === 'FAIR' ? 'text-amber-600' : 'text-red-600'}`}>{dcp.score}</span>
                        <span className={`text-[9px] font-bold px-1.5 rounded ${dcp.verdict === 'EXCELLENT' ? 'bg-green-100 text-green-700' : dcp.verdict === 'GOOD' ? 'bg-blue-100 text-blue-700' : dcp.verdict === 'FAIR' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{dcp.verdict}</span>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Missing Digit Groups */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="digit-groups">
          <div className="bg-gradient-to-r from-rose-600 to-red-700 px-5 py-3">
            <h2 className="text-white font-bold text-lg">Last-Digit Group Analysis — Last 5 Draws</h2>
            <p className="text-white/70 text-xs">Groups where NO number appeared in the last 5 draws have higher chances</p>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-10 gap-2">
              {data.digit_groups.map(dg => (
                <div key={dg.digit} className={`border rounded-lg p-3 text-center ${dg.all_missing ? 'bg-red-50 border-red-300 ring-2 ring-red-400' : 'bg-gray-50 border-gray-200'}`} data-testid={`digit-${dg.digit}`}>
                  <div className={`font-bold text-lg ${dg.all_missing ? 'text-red-600' : 'text-gray-700'}`}>
                    Digit {dg.digit}
                  </div>
                  <div className="flex flex-wrap justify-center gap-1 mt-1.5">
                    {dg.numbers.map(n => {
                      const appearedEntry = dg.appeared.find(a => a.number === n);
                      const isMissing = !appearedEntry;
                      return (
                        <div key={n} className="flex flex-col items-center">
                          <span className={`inline-flex w-7 h-7 rounded-full items-center justify-center font-mono font-bold text-[10px] ${isMissing ? (dg.all_missing ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-500') : 'bg-green-500 text-white'}`}>{n}</span>
                          {appearedEntry && <span className="text-[8px] font-bold text-green-700">{appearedEntry.count}x</span>}
                        </div>
                      );
                    })}
                  </div>
                  <div className="text-[10px] mt-1.5 font-bold">
                    {dg.all_missing ? (
                      <span className="text-red-600">ALL MISSING</span>
                    ) : (
                      <span className="text-gray-500">{dg.appeared_count} nums, {dg.total_appearances}x</span>
                    )}
                  </div>
                  {/* LS Digit Signal */}
                  {dg.ls_count > 0 && (
                    <div className={`text-[9px] mt-1 px-1.5 py-0.5 rounded ${dg.ls_signal === 'STRONG' ? 'bg-orange-100 text-orange-800 font-bold' : dg.ls_signal === 'MODERATE' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-500'}`} data-testid={`ls-signal-${dg.digit}`}>
                      LS: {dg.ls_count}x ({dg.ls_numbers?.join(',')})
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Smart Picks */}
        {data.smart_picks && data.smart_picks.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="smart-picks">
            <div className="bg-gradient-to-r from-emerald-600 to-teal-700 px-5 py-3">
              <div className="flex items-center gap-3">
                <Target size={20} weight="fill" className="text-white" />
                <div>
                  <h2 className="text-white font-bold text-lg">Smart Picks — Cross-Referenced Combinations</h2>
                  <p className="text-white/70 text-xs">
                    Fixed: B1=[2,3] B5=[45] (None) | Middle from H+M+L | LS1=[2,3] LS2=[9,11] | Score 100
                  </p>
                </div>
              </div>
            </div>
            {/* Date Analysis Signal Banner */}
            {data.date_analysis && (
              <div className="bg-gradient-to-r from-violet-50 to-purple-50 px-5 py-2.5 border-b border-violet-200" data-testid="date-signal-banner">
                <div className="flex items-center gap-3 flex-wrap text-[11px]">
                  <span className="font-bold text-violet-800">Date Signal ({data.date_analysis.next_draw_date})</span>
                  <span className="text-violet-600">
                    {data.date_analysis.same_date_draws} historical draws |
                    Missing DGs: {data.date_analysis.date_missing_dgs.length > 0 ? data.date_analysis.date_missing_dgs.join(', ') : 'None'} |
                    Top recurring: {data.date_analysis.date_top_numbers.slice(0, 3).map(t => `#${t.number}(${t.count}x)`).join(', ')}
                  </span>
                </div>
              </div>
            )}
            <div className="p-4 space-y-3">
              {data.smart_picks.map((sp, si) => (
                <div key={si} className={`border rounded-lg overflow-hidden ${si === 0 ? 'ring-2 ring-emerald-500 border-emerald-500' : 'border-gray-200'}`} data-testid={`smart-${si}`}>
                  <div className={`px-4 py-3 flex items-center gap-4 ${si === 0 ? 'bg-emerald-50' : 'bg-gray-50'}`}>
                    <span className={`inline-flex w-8 h-8 rounded-full items-center justify-center font-bold text-sm ${si === 0 ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-700'}`}>#{sp.rank}</span>
                    <div className="flex items-center gap-2">
                      {sp.ball_details.map((bd, bi) => (
                        <div key={bi} className="flex flex-col items-center">
                          <span className="text-[8px] text-gray-400 font-bold">{bd.position}</span>
                          <span className={`inline-flex w-10 h-10 rounded-full ${classColors[bd.class]?.bg} ${classColors[bd.class]?.text} items-center justify-center font-mono font-bold text-sm ${bd.fixed ? 'ring-2 ring-emerald-400' : ''} ${bd.date_dg_missing ? 'ring-2 ring-violet-400' : ''}`}>{bd.number}</span>
                          <span className={`text-[9px] font-bold mt-0.5 px-1 rounded ${classColors[bd.class]?.tag}`}>{classColors[bd.class]?.letter || bd.class[0]} {bd.times_cy}x</span>
                          <span className="text-[9px] text-gray-400">{bd.gap}d</span>
                          {bd.digit_group_missing && <span className="text-[8px] text-red-500 font-bold">D{bd.digit_group} miss</span>}
                          {bd.date_dg_missing && <span className="text-[8px] text-violet-600 font-bold">DG{bd.digit_group} date</span>}
                          {bd.date_recurring && <span className="text-[8px] text-violet-600 font-bold">{bd.date_freq}x recur</span>}
                          {bd.fixed && <span className="text-[8px] text-emerald-600 font-bold">FIXED</span>}
                        </div>
                      ))}
                      <span className="text-gray-300 mx-1">+</span>
                      {sp.lucky_stars.map((ls, li) => (
                        <div key={li} className="flex flex-col items-center">
                          <span className="inline-flex w-10 h-10 rounded-full bg-amber-500 text-white items-center justify-center font-mono font-bold text-sm">{ls}</span>
                          <span className="text-[9px] text-amber-600 font-bold mt-0.5">LS</span>
                        </div>
                      ))}
                    </div>
                    <div className="ml-auto flex items-center gap-4 flex-wrap">
                      <div className="flex gap-1">
                        {Object.entries(sp.class_mix).map(([cls, cnt]) => (
                          <span key={cls} className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${classColors[cls]?.tag}`}>{classColors[cls]?.letter || cls[0]}:{cnt}</span>
                        ))}
                      </div>
                      {sp.missing_groups_hit > 0 && (
                        <span className="text-[10px] font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded">
                          {sp.missing_groups_hit} miss grp
                        </span>
                      )}
                      {sp.date_missing_hit > 0 && (
                        <span className="text-[10px] font-bold text-violet-700 bg-violet-100 px-2 py-0.5 rounded">
                          {sp.date_missing_hit} date DG
                        </span>
                      )}
                      {sp.date_recurring_hit > 0 && (
                        <span className="text-[10px] font-bold text-purple-700 bg-purple-100 px-2 py-0.5 rounded">
                          {sp.date_recurring_hit} recur
                        </span>
                      )}
                      <div className="text-right">
                        <span className={`font-bold text-lg font-mono ${sp.verdict === 'EXCELLENT' ? 'text-emerald-600' : 'text-blue-600'}`}>{sp.score}</span>
                        <div className={`text-[9px] font-bold px-1.5 rounded ${sp.verdict === 'EXCELLENT' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>{sp.verdict}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
          </TabsContent>

          <TabsContent value="last-200" className="mt-0">
            <ClassMixLast200 />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
